import { HYPERSWITCH_DEFECT_CODE } from '../../../shared/constants/paymentCodes';

export const HYPERSWITCH_SUBSCRIPTION_SERVICE = 'round_hyperswitch';

const HYPERSWITCH_GATEWAY = 'hyperswitch';

export type HyperswitchIntentResult
  = | { kind: 'intent'; id: string; clientSecret: string; publishableKey: string }
  /** The backend answered on another rail. Mounting the Hyperswitch SDK on it would fail later. */
    | { kind: 'wrong-gateway'; gateway: string }
    | { kind: 'incomplete' };

/**
 * The SDK cannot mount without both the client secret and the publishable key, and both arrive with
 * the intent rather than from the config request.
 */
export function readHyperswitchIntent(paymentIntent: unknown): HyperswitchIntentResult {
  const intent = paymentIntent as Record<string, unknown> | null | undefined;
  const gateway = typeof intent?.gateway === 'string' ? intent.gateway : '';

  if (gateway !== HYPERSWITCH_GATEWAY) {
    return { kind: 'wrong-gateway', gateway };
  }

  const id = intent?.id;
  const clientSecret = intent?.client_secret;
  const publishableKey = intent?.publishable_key;

  if (typeof id !== 'string' || typeof clientSecret !== 'string' || typeof publishableKey !== 'string'
    || !id || !clientSecret || !publishableKey) {
    return { kind: 'incomplete' };
  }

  return { kind: 'intent', id, clientSecret, publishableKey };
}

/**
 * `confirmPayment` resolves for every outcome, including a decline that arrives over HTTP 200, so
 * the caller must read the kind rather than look for a thrown error.
 *
 * `no-payment-method` is the dangerous one: the payment is authorised and capturable, but nothing
 * was stored to charge a renewal against, and no field in the response says so. It must reach
 * Sentry under its own name rather than the generic card failure.
 */
const FAILURE_CODE: Record<string, string> = {
  'no-payment-method': HYPERSWITCH_DEFECT_CODE.NO_PAYMENT_METHOD,
  'intent-unusable': HYPERSWITCH_DEFECT_CODE.INTENT_UNUSABLE,
  'requires-action': 'hyperswitch_requires_action',
  'declined': 'card_declined_by_form',
  'unanswered': 'hyperswitch_confirm_unanswered',
};

export function hyperswitchFailureCode(kind: string, code?: string | null): string {
  return kind === 'declined' && code ? code : FAILURE_CODE[kind] ?? 'hyperswitch_unknown';
}
